import type { CarrierNew } from '@eti/schema-types';
import { EventSegmentType as EventSegmentTypeEnum } from '@eti/schema-types';
import { getStopoverType } from '../../../../../../utils/format';
import { boundIncludesTrainSegments } from '../../../../../../widgets/trip-details/utils/format';
import { getUniqueMarketingCarriersFromSegments } from '../../../../../../utils/carriers';
import type { RebookingTripBoundsType } from '../../../../../change-trip/models';
import type {
  BoundsType,
  EventSegmentType,
  SegmentLocationType,
  TripSegmentType,
} from '../../../../models';
import type { TrainSegmentType } from './ItineraryDetailsLocationTime';

/**
 * The function `getFinalSegments` takes an array of `bounds` and returns an array of final segments with additional properties.
 * @param {BoundType[]} bounds - The `bounds` parameter is an array of `BoundType` objects. Each
 * `BoundType` object represents a bound or direction of travel and contains an array of segments. Each
 * segment represents a specific leg or event in the travel itinerary.
 * @returns an array of objects of type `FinalSegmentsType[]`.
 */

export type FinalSegmentsType = Partial<TripSegmentType> & {
  allMarketingCarriers: {
    name: string;
    code: string;
  }[];
  boundNumber: number;
  destination: SegmentLocationType;
  isSelfTransfer: boolean;
  marketingCarrier: CarrierNew;
  origin: SegmentLocationType;
  segmentId?: string;
  stopoverType?: string | null;
  stops: number;
  totalDuration: number;
  totalNumberOfTechnicalStops: number;
  trainSegment: TrainSegmentType;
};

export const getFinalSegments = (bounds: BoundsType | RebookingTripBoundsType) => {
  return bounds.reduce((acc, bound, index) => {
    if (bound && bound.segments) {
      const { segments } = bound;
      const trainSegment = boundIncludesTrainSegments(segments);
      const allMarketingCarriers = getUniqueMarketingCarriersFromSegments(segments);

      const eventSegments = segments.filter(
        (segment): segment is EventSegmentType => segment?.__typename === 'EventSegment',
      );
      const tripSegments = segments.filter(
        (segment): segment is TripSegmentType => segment.__typename === 'TripSegment',
      ) as TripSegmentType[];

      const isSelfTransfer = eventSegments
        .map(
          (filteredSegment) =>
            (filteredSegment as EventSegmentType)?.types?.some((eventType) => {
              return (
                (eventType as any)?.type === EventSegmentTypeEnum.SelfTransfer ||
                (eventType as any) === EventSegmentTypeEnum.SelfTransfer
              );
            }),
        )
        .includes(true);

      const totalDuration = segments.reduce<number>(
        (totalDurationAcc, { duration }) =>
          duration && Number.isFinite(duration) ? totalDurationAcc + duration : totalDurationAcc,
        0,
      );

      const totalNumberOfTechnicalStops = tripSegments.reduce<number>(
        (totalNumberOfTechnicalStopsAcc, { numberOfTechnicalStops }) =>
          Number.isFinite(numberOfTechnicalStops)
            ? totalNumberOfTechnicalStopsAcc + numberOfTechnicalStops
            : totalNumberOfTechnicalStopsAcc,
        0,
      );

      const stopoverType = getStopoverType(segments);
      const firstTripSegment = segments[0] as TripSegmentType;

      acc.push({
        boundNumber: index,
        ...firstTripSegment,
        arrivedAt: (segments[segments.length - 1] as TripSegmentType)?.arrivedAt,
        destination: (segments[segments.length - 1] as TripSegmentType)?.destination,
        isSelfTransfer,
        stops: segments.filter((segment) => segment?.__typename === 'EventSegment').length,
        stopoverType,
        totalDuration,
        totalNumberOfTechnicalStops,
        allMarketingCarriers,
        trainSegment,
      });
      return acc;
    }
    return acc;
  }, [] as FinalSegmentsType[]);
};
